import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, ArrowRight, Check, CircleAlert, GripVertical, Loader2, X } from 'lucide-react';
import { useTourStore } from './tourStore';
import { TOURS, type TourStep } from './steps';

const NO_STEPS: TourStep[] = [];
import { useNavigationStore } from '../store/navigationStore';
import { useAuthStore } from '../store/authStore';
import { proofStep, type CheckFeedback } from '../api/academy';
import { findByText } from './findByText';

// Celonis-style clickthrough overlay. Dark floating card + purple spotlight.
// The whole overlay is pointer-events:none so the live app stays clickable
// underneath — steps that say "[Haz clic ...]" rely on that.

const BRAND = '#7C3AED';
const CARD_W = 380;
const PAD = 8; // spotlight padding around the target rect
const GAP = 14; // gap between spotlight and card

// Resolve as soon as any selector matches (priority = array order within one
// scan). MutationObserver-driven so elements are found the frame they mount
// instead of on a polling interval; the slow interval is only a safety net.
function waitForAny(
  selectors: string[],
  timeoutMs: number,
  isCancelled: () => boolean,
): Promise<HTMLElement | null> {
  return waitUntil(() => {
    for (const sel of selectors) {
      const el = document.querySelector<HTMLElement>(sel);
      if (el) return el;
    }
    return null;
  }, timeoutMs, isCancelled);
}

function waitUntil(
  find: () => HTMLElement | null,
  timeoutMs: number,
  isCancelled: () => boolean,
): Promise<HTMLElement | null> {
  return new Promise((resolve) => {
    let done = false;
    let cleanup = () => {};
    const finish = (el: HTMLElement | null) => {
      if (done) return;
      done = true;
      cleanup();
      resolve(el);
    };
    const check = () => {
      if (isCancelled()) return finish(null);
      const el = find();
      if (el) finish(el);
    };
    const obs = new MutationObserver(check);
    obs.observe(document.body, { childList: true, subtree: true, attributes: true });
    const iv = window.setInterval(check, 250);
    const to = window.setTimeout(() => finish(null), timeoutMs);
    cleanup = () => {
      obs.disconnect();
      window.clearInterval(iv);
      window.clearTimeout(to);
    };
    check();
  });
}

function waitFor(
  selector: string,
  timeoutMs: number,
  isCancelled: () => boolean,
): Promise<HTMLElement | null> {
  return waitForAny([selector], timeoutMs, isCancelled);
}

// Pre-type into a React-controlled input: the value must go through the
// native setter + an `input` event, otherwise React ignores it. Focused
// afterwards so the presenter can just hit Enter.
function fillInput(el: HTMLElement, text: string) {
  if (!(el instanceof HTMLInputElement) && !(el instanceof HTMLTextAreaElement)) return;
  const proto = el instanceof HTMLTextAreaElement
    ? HTMLTextAreaElement.prototype
    : HTMLInputElement.prototype;
  Object.getOwnPropertyDescriptor(proto, 'value')?.set?.call(el, text);
  el.dispatchEvent(new Event('input', { bubbles: true }));
  el.focus();
}

interface Rect { top: number; left: number; width: number; height: number }

function sameRect(a: Rect | null, b: Rect | null): boolean {
  if (!a || !b) return a === b;
  return a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height;
}

export const TourOverlay: React.FC = () => {
  const {
    active, stepIndex, stop, next, prev,
    source, academySteps, academyLabel, enrollmentId, proofedSteps, markProofed,
  } = useTourStore();
  const start = useTourStore((s) => s.start);
  // Demo storyboards are per tenant: the signed-in tenant picks its
  // storyboard in TOURS (FERCO, IPSA, ...).
  const tenantId = useAuthStore((s) => s.user?.tenant_id);
  const demoSteps = (tenantId && TOURS[tenantId]) || NO_STEPS;
  const steps = source === 'demo' ? demoSteps : academySteps;
  const step = steps[stepIndex];
  const isLast = stepIndex === steps.length - 1;

  // Proof gating (PAI Academy): a proof step blocks Next until the academy
  // service confirms the build against the Kitchen's real state.
  const stepProofed = !step?.proof || proofedSteps.has(step.id);
  const [proofing, setProofing] = useState(false);
  const [proofChecks, setProofChecks] = useState<CheckFeedback[] | null>(null);

  const targetElRef = useRef<HTMLElement | null>(null);
  const [rect, setRect] = useState<Rect | null>(null);
  const [ready, setReady] = useState(false);
  // Slow-resolve fallback: if the target takes >400ms to appear (heavy page
  // mount, cold Kitchen), show the card centered instead of dead air; it
  // glides to the anchor once the target lands. Fast steps never see this.
  const [slowResolve, setSlowResolve] = useState(false);
  // Bumped on every step resolve. `ready` can flip false->true inside one
  // React batch (steps with no async work), which leaves it unchanged as an
  // effect dep — this tick guarantees the positioning effect re-runs.
  const [resolveTick, setResolveTick] = useState(0);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [cardPos, setCardPos] = useState<{ top: number; left: number } | null>(null);
  // Draggable card: the recipe card can land exactly on top of the modal the
  // step asks you to use. Dragging from the header moves it; once moved, the
  // auto-positioner leaves it alone until the next step.
  const draggedRef = useRef(false);
  const [dragging, setDragging] = useState(false);

  const onDragStart = (e: React.MouseEvent) => {
    if (!cardRef.current || (e.target as HTMLElement).closest('button')) return;
    e.preventDefault();
    const rect0 = cardRef.current.getBoundingClientRect();
    const offX = e.clientX - rect0.left;
    const offY = e.clientY - rect0.top;
    setDragging(true);
    const move = (ev: MouseEvent) => {
      draggedRef.current = true;
      const vw = window.innerWidth, vh = window.innerHeight;
      const w = cardRef.current?.offsetWidth ?? CARD_W;
      const h = cardRef.current?.offsetHeight ?? 200;
      setCardPos({
        top: Math.min(Math.max(ev.clientY - offY, 4), vh - h - 4),
        left: Math.min(Math.max(ev.clientX - offX, 4), vw - w - 4),
      });
    };
    const up = () => {
      setDragging(false);
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  // Auto-start via ?tour=1 (shareable demo link). The storyboard references
  // apps seeded only in its demo tenant, so tenants without a TOURS entry get
  // the param stripped without starting.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('tour') === '1') {
      params.delete('tour');
      const qs = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
      const tid = useAuthStore.getState().user?.tenant_id;
      if (tid && TOURS[tid]) start();
    }
  }, [start]);

  const goNext = () => {
    if (!stepProofed) return; // proof-gated: Verificar first
    if (isLast) stop();
    else next();
  };

  const runProof = async () => {
    if (!enrollmentId || !step) return;
    setProofing(true);
    try {
      const res = await proofStep(enrollmentId, step.id);
      setProofChecks(res.checks);
      if (res.passed) markProofed(step.id);
    } catch (err) {
      setProofChecks([{
        label: 'Verificacion',
        passed: false,
        feedback: 'No se pudo contactar al servicio de Academy. Intenta de nuevo.',
      }]);
      console.warn('academy proof failed', err);
    } finally {
      setProofing(false);
    }
  };

  // Resolve the current step: navigate, best-effort click, find the target.
  // Layout effect so the reset (hide card) commits before the browser paints —
  // a passive effect would flash one frame of the old card with the new step's
  // counter while the async resolve is still pending.
  useLayoutEffect(() => {
    if (!active) return;
    if (!step) { stop(); return; }
    let cancelled = false;
    const isCancelled = () => cancelled;

    setReady(false);
    setSlowResolve(false);
    setRect(null);
    setCardPos(null);
    setProofChecks(null);
    draggedRef.current = false;
    targetElRef.current = null;
    const slowTimer = window.setTimeout(() => setSlowResolve(true), 400);

    (async () => {
      const nav = useNavigationStore.getState();
      if (step.page && nav.currentPage !== step.page) nav.navigateTo(step.page);

      if (step.target) {
        // Race the target against the setup click: if the destination is
        // already (or becomes) visible — e.g. the user clicked the highlighted
        // tile themselves — we anchor immediately instead of blocking on a
        // clickSelector that may never appear (the old serial wait cost a
        // full timeout on every tile step).
        let el: HTMLElement | null;
        if (step.clickSelector) {
          el = await waitForAny([step.target, step.clickSelector], 2500, isCancelled);
          if (cancelled) return;
          if (el && !el.matches(step.target)) {
            el.click();
            el = await waitFor(step.target, 8000, isCancelled);
          } else if (!el) {
            // Neither the target nor the setup click ever appeared (the
            // presenter skipped the instructed interaction) — give the target
            // one short last chance, then show the card centered instead of
            // blocking the tour for the full timeout.
            el = await waitFor(step.target, 1500, isCancelled);
          }
        } else {
          el = await waitFor(step.target, 8000, isCancelled);
        }
        if (cancelled) return;
        targetElRef.current = el;
        if (el) el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        if (step.fill) {
          const input = step.fill.selector === step.target
            ? el
            : await waitFor(step.fill.selector, 3000, isCancelled);
          if (cancelled) return;
          if (input) fillInput(input, step.fill.text);
        }
      } else if (step.targetText) {
        // Label-addressed step (Assistant guides): a short wait, then a
        // centered card if the control is not on screen — the guide still
        // reads, it just has nothing to point at.
        const text = step.targetText;
        const el = await waitUntil(() => findByText(text), 3000, isCancelled);
        if (cancelled) return;
        targetElRef.current = el;
        if (el) el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      } else if (step.clickSelector) {
        const clickEl = await waitFor(step.clickSelector, 2500, isCancelled);
        if (cancelled) return;
        if (clickEl) clickEl.click();
      }
      window.clearTimeout(slowTimer);
      setReady(true);
      setResolveTick((t) => t + 1);
    })();

    return () => { cancelled = true; window.clearTimeout(slowTimer); };
  }, [active, stepIndex, source]);

  // Track the target rect while the step is visible (layout shifts, resize,
  // async data pushing the element around).
  useEffect(() => {
    if (!active || !ready) return;
    const update = () => {
      let el = targetElRef.current;
      if (el && !el.isConnected && (step?.target || step?.targetText)) {
        el = step.target
          ? document.querySelector<HTMLElement>(step.target)
          : findByText(step.targetText!);
        targetElRef.current = el;
      }
      if (!el || !el.isConnected) { setRect((r) => (r === null ? r : null)); return; }
      const b = el.getBoundingClientRect();
      const nextRect = { top: b.top, left: b.left, width: b.width, height: b.height };
      setRect((r) => (sameRect(r, nextRect) ? r : nextRect));
    };
    update();
    const iv = window.setInterval(update, 200);
    window.addEventListener('resize', update);
    return () => { window.clearInterval(iv); window.removeEventListener('resize', update); };
  }, [active, ready, stepIndex]);

  // "[Haz clic ...]" steps: clicking the spotlighted element advances.
  useEffect(() => {
    if (!active || !ready || !step?.advanceOnTargetClick) return;
    const el = targetElRef.current;
    if (!el) return;
    const onClick = () => { window.setTimeout(() => useTourStore.getState().next(), 250); };
    el.addEventListener('click', onClick);
    return () => el.removeEventListener('click', onClick);
  }, [active, ready, stepIndex]);

  // Keyboard: arrows navigate, Esc exits. Ignored while typing.
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (e.key === 'Escape') stop();
      else if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, stepIndex]);

  // Position the card next to the spotlight (or center it). A user-dragged
  // card stays where they put it for the rest of the step.
  useLayoutEffect(() => {
    if (!active || (!ready && !slowResolve) || draggedRef.current) return;
    const card = cardRef.current;
    if (!card) return;
    const ch = card.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

    if (!rect) {
      setCardPos({ top: Math.max(24, (vh - ch) / 2), left: (vw - CARD_W) / 2 });
      return;
    }
    const spot = {
      top: rect.top - PAD, left: rect.left - PAD,
      width: rect.width + PAD * 2, height: rect.height + PAD * 2,
    };
    const space = {
      right: vw - (spot.left + spot.width),
      left: spot.left,
      bottom: vh - (spot.top + spot.height),
      top: spot.top,
    };
    const fits: Record<string, boolean> = {
      right: space.right >= CARD_W + GAP + 12,
      left: space.left >= CARD_W + GAP + 12,
      bottom: space.bottom >= ch + GAP + 12,
      top: space.top >= ch + GAP + 12,
    };
    const order = step?.placement
      ? [step.placement, 'bottom', 'right', 'left', 'top']
      : ['right', 'bottom', 'left', 'top'];
    const side = order.find((s) => fits[s]) || 'center';

    let top: number; let left: number;
    if (side === 'right') {
      top = clamp(spot.top, 12, vh - ch - 12);
      left = spot.left + spot.width + GAP;
    } else if (side === 'left') {
      top = clamp(spot.top, 12, vh - ch - 12);
      left = spot.left - CARD_W - GAP;
    } else if (side === 'bottom') {
      top = spot.top + spot.height + GAP;
      left = clamp(spot.left, 12, vw - CARD_W - 12);
    } else if (side === 'top') {
      top = spot.top - ch - GAP;
      left = clamp(spot.left, 12, vw - CARD_W - 12);
    } else {
      top = Math.max(24, (vh - ch) / 2);
      left = (vw - CARD_W) / 2;
    }
    setCardPos({ top, left });
  }, [active, ready, slowResolve, rect, stepIndex, resolveTick, proofChecks]);

  if (!active || !step) return null;

  const arrowBtn = (disabled: boolean): React.CSSProperties => ({
    width: 36, height: 27, borderRadius: 4, border: 'none',
    backgroundColor: BRAND, color: '#fff',
    cursor: disabled ? 'default' : 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    opacity: disabled ? 0.35 : 1, padding: 0, flexShrink: 0,
  });

  return createPortal(
    <div data-tour-skip style={{ position: 'fixed', inset: 0, zIndex: 20000, pointerEvents: 'none' }}>
      <style>{`
        @keyframes nexusTourPing {
          0% { box-shadow: 0 0 0 0 rgba(124, 58, 237, 0.5); }
          100% { box-shadow: 0 0 0 12px rgba(124, 58, 237, 0); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Dimmer — a cutout around the target via the giant-shadow trick,
          or a plain veil for centered steps (and while a slow page is still
          mounting the target). */}
      {(ready || slowResolve) && (rect ? (
        <div style={{
          position: 'fixed',
          top: rect.top - PAD, left: rect.left - PAD,
          width: rect.width + PAD * 2, height: rect.height + PAD * 2,
          borderRadius: 6,
          boxShadow: '0 0 0 200vmax rgba(4, 8, 20, 0.5)',
          transition: 'top 180ms ease-out, left 180ms ease-out, width 180ms ease-out, height 180ms ease-out',
        }} />
      ) : (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(4, 8, 20, 0.45)' }} />
      ))}

      {/* Pulsing spotlight ring */}
      {ready && rect && (
        <div style={{
          position: 'fixed',
          top: rect.top - PAD, left: rect.left - PAD,
          width: rect.width + PAD * 2, height: rect.height + PAD * 2,
          borderRadius: 6, border: `2px solid ${BRAND}`,
          animation: 'nexusTourPing 1.8s ease-out infinite',
          transition: 'top 180ms ease-out, left 180ms ease-out, width 180ms ease-out, height 180ms ease-out',
        }} />
      )}

      {/* Card */}
      {(ready || slowResolve) && (
        <div
          ref={cardRef}
          style={{
            position: 'fixed',
            top: cardPos?.top ?? -9999, left: cardPos?.left ?? -9999,
            visibility: cardPos ? 'visible' : 'hidden',
            width: CARD_W, maxWidth: 'calc(100vw - 24px)',
            backgroundColor: '#0F1824', border: '1px solid #1E2D42', borderRadius: 6,
            boxShadow: '0 16px 48px rgba(0, 0, 0, 0.5)',
            padding: '14px 16px 12px', pointerEvents: 'auto',
            // No transition while dragging — the card must stick to the
            // cursor, not chase it 180ms behind.
            transition: dragging ? 'none' : 'top 180ms ease-out, left 180ms ease-out',
          }}
        >
          <div
            onMouseDown={onDragStart}
            title="Arrastra para mover la tarjeta"
            style={{
              display: 'flex', alignItems: 'center', marginBottom: 8,
              cursor: dragging ? 'grabbing' : 'grab', userSelect: 'none',
              margin: '-14px -16px 8px', padding: '14px 16px 0',
            }}
          >
            <GripVertical size={12} color="#475569" style={{ marginRight: 6, flexShrink: 0 }} />
            <span style={{
              fontSize: 9.5, fontWeight: 700, letterSpacing: '0.09em',
              color: '#A78BFA', textTransform: 'uppercase',
            }}>
              {source === 'demo' ? 'Demo guiado' : academyLabel}
            </span>
            <button
              onClick={stop}
              aria-label="Cerrar recorrido"
              style={{
                marginLeft: 'auto', border: 'none', background: 'transparent',
                color: '#64748B', cursor: 'pointer', padding: 2, display: 'flex',
              }}
            >
              <X size={15} />
            </button>
          </div>

          <div style={{ fontSize: 14, fontWeight: 600, color: '#F8FAFC', marginBottom: 6 }}>
            {step.title}
          </div>
          <div style={{
            fontSize: 12.5, color: '#CBD5E1', lineHeight: 1.55,
            whiteSpace: 'pre-line', marginBottom: step.action ? 8 : 12,
          }}>
            {step.body}
          </div>
          {step.action && (
            <div style={{ fontSize: 12.5, fontWeight: 700, color: '#A78BFA', marginBottom: 12 }}>
              {step.action}
            </div>
          )}

          {/* Proofing results (PAI Academy) */}
          {step.proof && proofChecks && (
            <div style={{
              marginBottom: 12, padding: '8px 10px', borderRadius: 4,
              backgroundColor: '#0A121C', border: '1px solid #1E2D42',
              display: 'flex', flexDirection: 'column', gap: 6,
            }}>
              {proofChecks.map((c, i) => (
                <div key={i} style={{ display: 'flex', gap: 7, alignItems: 'flex-start' }}>
                  {c.passed
                    ? <Check size={13} color="#34D399" style={{ flexShrink: 0, marginTop: 1 }} />
                    : <CircleAlert size={13} color="#F87171" style={{ flexShrink: 0, marginTop: 1 }} />}
                  <div>
                    <div style={{
                      fontSize: 11.5, fontWeight: 600,
                      color: c.passed ? '#6EE7B7' : '#FCA5A5',
                    }}>
                      {c.label}
                    </div>
                    {!c.passed && c.feedback && (
                      <div style={{ fontSize: 11, color: '#94A3B8', lineHeight: 1.45 }}>
                        {c.feedback}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11, color: '#64748B' }}>
              {stepIndex + 1} / {steps.length}
            </span>
            {step.proof && (
              <button
                onClick={runProof}
                disabled={proofing}
                style={{
                  marginLeft: 4, height: 27, borderRadius: 4, border: 'none',
                  padding: '0 12px', display: 'flex', alignItems: 'center', gap: 6,
                  backgroundColor: stepProofed ? '#064E3B' : BRAND,
                  color: stepProofed ? '#6EE7B7' : '#fff',
                  fontSize: 12, fontWeight: 600,
                  cursor: proofing ? 'default' : 'pointer', flexShrink: 0,
                  opacity: proofing ? 0.7 : 1,
                }}
              >
                {proofing
                  ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />
                  : stepProofed ? <Check size={13} /> : null}
                {stepProofed ? 'Verificado' : 'Verificar'}
              </button>
            )}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              <button onClick={prev} disabled={stepIndex === 0} style={arrowBtn(stepIndex === 0)} aria-label="Anterior">
                <ArrowLeft size={15} />
              </button>
              {isLast ? (
                <button
                  onClick={stop}
                  style={{
                    height: 27, borderRadius: 4, border: 'none', padding: '0 14px',
                    backgroundColor: BRAND, color: '#fff', fontSize: 12, fontWeight: 600,
                    cursor: 'pointer', flexShrink: 0,
                  }}
                >
                  Finalizar
                </button>
              ) : (
                <button
                  onClick={goNext}
                  style={arrowBtn(!stepProofed)}
                  aria-label="Siguiente"
                  title={!stepProofed ? 'Verifica este paso para continuar' : undefined}
                >
                  <ArrowRight size={15} />
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
};

export default TourOverlay;
